import type { AiEntryContext } from "@/lib/ai/client-settings";
import type {
	OdinAiAnalysisStatus,
	OdinAiEvidenceType,
} from "@/lib/ai/contracts/evidence";
import type {
	AiBrowsingSummary,
	AiBrowsingSummaryItem,
} from "@/lib/ai/browsing-summary";
import { formatAddressDisplayLabel, formatTokenDisplayLabel } from "@/lib/ai/entity-display";
import { parseAiRouteMeta } from "@/lib/ai/entity-route";
import type {
	AiBrowsingEvent,
	AiChatSessionRecord,
} from "@/lib/ai/storage/types";

const pageLabelMap: Record<string, string> = {
	"/plaza": "广场",
	"/data": "数据概览",
	"/scan": "交易监控",
	"/address": "地址分析",
	"/profile": "我",
	"/discover": "发现",
};

export function getAiContextLabel(pathname: string) {
	if (pathname.startsWith("/plaza/token/")) {
		return "Token 详情";
	}

	if (pathname.startsWith("/address/")) {
		return "地址详情";
	}

	return pageLabelMap[pathname] ?? pathname;
}

export interface AiContextPack {
	entryContext: AiEntryContext | null;
	recentBrowsing: AiBrowsingEvent[];
	activeSession: Pick<AiChatSessionRecord, "id" | "title" | "updatedAt" | "lastMessageAt"> | null;
	marketQuote: AiMarketQuoteSnapshot | null;
	viewerIdentity: AiViewerIdentitySnapshot | null;
	selectedEntities: AiContextEntity[];
	entityResolution: AiEntityResolutionSnapshot | null;
	frontendToolResults: AiFrontendToolResult[];
	sessionMemory: AiSessionMemorySnapshot | null;
	entityMemories: AiEntityMemorySnapshot[];
	relevantBrowsing: AiBrowsingEvent[];
	summary: string;
}

export interface AiMarketQuoteSnapshot {
	btcUsd: number;
	fetchedAt: string;
	source: string;
	stale: boolean;
}

export interface AiViewerIdentitySnapshot {
	isAuthenticated: boolean;
	authStatus: "anonymous" | "restoring" | "ready";
	principal?: string;
	btcAddress?: string;
	walletType?: string;
}

export interface AiFrontendToolResult {
	toolId: string;
	entityKey: string;
	status: "ok" | "partial" | "error";
	summary: string;
	payload?: Record<string, unknown>;
	error?: string;
	fetchedAt: string;
	displayMode?: "snapshot" | "disambiguation" | "hidden";
	cacheTtlMs?: number;
	evidenceRef?: string;
	evidenceType?: OdinAiEvidenceType;
	analysisStatus?: OdinAiAnalysisStatus;
}

export interface AiContextEntity {
	key: string;
	entityType: NonNullable<AiBrowsingEvent["entityType"]>;
	entityId: string;
	label: string;
	source: "entry-context" | "browsing" | "message" | "session";
	score: number;
}

export interface AiEntityResolutionSnapshot {
	latestUserText?: string;
	carryoverEntityKeys: string[];
	selectedEntitySources: Array<AiContextEntity["source"]>;
	dominantEntityKey?: string;
	dominantEntityType?:
		| "token"
		| "address"
		| "market"
		| "global_market"
		| "asset"
		| "exchange"
		| "chain"
		| "sns"
		| "canister";
	dominantEntitySource?: AiContextEntity["source"];
	dominantEntityMode?: "explicit-message" | "follow-up-carryover" | "context-fallback";
	companionEntityKeys: string[];
	focusByType: Partial<
		Record<
			| "token"
			| "address"
			| "market"
			| "global_market"
			| "asset"
			| "exchange"
			| "chain"
			| "sns"
			| "canister",
			string
		>
	>;
}

export interface AiSessionMemorySnapshot {
	summary: string;
	recentQuestions: string[];
	focusedEntityKeys: string[];
	focusByType: Partial<
		Record<
			| "token"
			| "address"
			| "market"
			| "global_market"
			| "asset"
			| "exchange"
			| "chain"
			| "sns"
			| "canister",
			string
		>
	>;
	dominantFocusEntityKey?: string;
	dominantFocusEntityMode?: "explicit-message" | "follow-up-carryover" | "context-fallback";
	lastUserMessage?: string;
	updatedAt?: number;
}

export interface AiEntityMemorySnapshot {
	key: string;
	entityType: NonNullable<AiBrowsingEvent["entityType"]>;
	entityId: string;
	label: string;
	summary: string;
	recentQuestions: string[];
	updatedAt?: number;
}

interface BuildAiSuggestionsOptions {
	entryContext: AiEntryContext | null;
	recentBrowsing?: AiBrowsingEvent[];
	browsingSummary?: AiBrowsingSummary | null;
	activeSession?: Pick<
		AiChatSessionRecord,
		"id" | "title" | "updatedAt" | "lastMessageAt"
	> | null;
}

function buildEntrySummaryItem(
	entryContext: AiEntryContext | null,
	browsingSummary: AiBrowsingSummary | null,
): AiBrowsingSummaryItem | null {
	if (!entryContext?.pathname) {
		return null;
	}

	const routeMeta = parseAiRouteMeta(entryContext.pathname);
	const summaryMatch = browsingSummary?.recentUnique.find((item) => {
		return (
			item.pathname === entryContext.pathname ||
			(Boolean(item.entityType) &&
				item.entityType === routeMeta.entityType &&
				item.entityId === routeMeta.entityId)
		);
	});

	if (summaryMatch) {
		return summaryMatch;
	}

	const entityType = routeMeta.entityType;
	const entityId = routeMeta.entityId;
	const displayLabel =
		entityType === "token"
			? formatTokenDisplayLabel(undefined, entityId)
			: entityType === "address"
				? formatAddressDisplayLabel(undefined, entityId)
				: entryContext.label;

	return {
		id: `entry:${entryContext.pathname}`,
		pathname: entryContext.pathname,
		label: displayLabel,
		displayLabel,
		source: entryContext.source,
		timestamp: entryContext.timestamp,
		firstVisitedAt: entryContext.timestamp,
		lastVisitedAt: entryContext.timestamp,
		visitCount: 1,
		score: 0,
		routeGroup: routeMeta.routeGroup,
		entityType,
		entityId,
		isDefaultEntry: entryContext.pathname === "/plaza",
		isLowSignal: entryContext.pathname === "/plaza",
	};
}

function appendSuggestion(
	suggestions: string[],
	nextSuggestion: string | null,
	limit = 3,
) {
	if (!nextSuggestion || suggestions.includes(nextSuggestion) || suggestions.length >= limit) {
		return;
	}

	suggestions.push(nextSuggestion);
}

export function buildAiSuggestions({
	entryContext,
	recentBrowsing = [],
	browsingSummary = null,
	activeSession = null,
}: BuildAiSuggestionsOptions) {
	const summary = browsingSummary ?? {
		recentUnique: recentBrowsing.map((item) => ({
			...item,
			displayLabel: item.label,
			visitCount: 1,
			firstVisitedAt: item.timestamp,
			lastVisitedAt: item.timestamp,
			score: 0,
		})),
		topEntities: [],
		recentTokens: recentBrowsing
			.filter((item) => item.entityType === "token")
			.map((item) => ({
				...item,
				displayLabel: item.label,
				visitCount: 1,
				firstVisitedAt: item.timestamp,
				lastVisitedAt: item.timestamp,
				score: 0,
			})),
		recentAddresses: recentBrowsing
			.filter((item) => item.entityType === "address")
			.map((item) => ({
				...item,
				displayLabel: item.label,
				visitCount: 1,
				firstVisitedAt: item.timestamp,
				lastVisitedAt: item.timestamp,
				score: 0,
			})),
		recentInsightPages: recentBrowsing
			.filter(
				(item) =>
					item.pathname.startsWith("/data") || item.pathname.startsWith("/scan"),
			)
			.map((item) => ({
				...item,
				displayLabel: item.label,
				visitCount: 1,
				firstVisitedAt: item.timestamp,
				lastVisitedAt: item.timestamp,
				score: 0,
			})),
	} satisfies AiBrowsingSummary;
	const entryItem = buildEntrySummaryItem(entryContext, browsingSummary);
	const tokenSeed =
		(entryItem?.entityType === "token" ? entryItem : null) ??
		summary.recentTokens[0] ??
		summary.topEntities.find((item) => item.entityType === "token") ??
		null;
	const addressSeed =
		(entryItem?.entityType === "address" ? entryItem : null) ??
		summary.recentAddresses[0] ??
		summary.topEntities.find((item) => item.entityType === "address") ??
		null;
	const marketSeed =
		summary.recentInsightPages[0] ??
		(entryItem &&
		(entryItem.pathname.startsWith("/data") || entryItem.pathname.startsWith("/scan"))
			? entryItem
			: null);
	const suggestions: string[] = [];

	if (!tokenSeed && !addressSeed && !marketSeed && !activeSession) {
		return [
			"今天 Odin 里最值得先看的方向有哪些？",
			"如果我是第一次用 Odin，应该先看什么？",
			"你建议我现在从哪个数据场景开始分析？",
		];
	}

	appendSuggestion(
		suggestions,
		tokenSeed
			? `继续分析 ${tokenSeed.displayLabel}，现在最值得先看什么？`
			: null,
	);
	appendSuggestion(
		suggestions,
		addressSeed
			? `看一下 ${addressSeed.displayLabel} 最近最值得关注的行为。`
			: null,
	);
	appendSuggestion(
		suggestions,
		tokenSeed
			? `针对 ${tokenSeed.displayLabel}，下一步应该优先看价格、流动性还是持仓？`
			: null,
	);
	appendSuggestion(
		suggestions,
		addressSeed
			? `${addressSeed.displayLabel} 更像短线交易者还是长期持有者？`
			: null,
	);
	appendSuggestion(
		suggestions,
		marketSeed
			? `基于你最近看的${marketSeed.displayLabel}，接下来最值得验证的信号是什么？`
			: "结合你最近浏览的内容，今天更值得先分析哪个代币或地址？",
	);

	if (suggestions.length >= 3) {
		return suggestions.slice(0, 3);
	}

	if (activeSession?.title) {
		appendSuggestion(
			suggestions,
			`继续刚才「${activeSession.title}」这条分析线，下一步先问什么？`,
		);
	}

	appendSuggestion(suggestions, "结合我最近浏览的内容，今天更值得先分析哪个代币或地址？");
	appendSuggestion(suggestions, "这个场景下最有效的分析切入点是什么？");
	appendSuggestion(suggestions, "请给我 3 条贴近当前页面的下一步建议。");

	return suggestions.slice(0, 3);
}
